import clsx from "classnames"
import { NodeKey } from "lexical"
import { GridBlock, GridSlotSize } from "common/types/graphql"
import { FrontendBlock, ImageAspectRatio } from "modules/editor/types"
import { ContentSummaryVariant } from "common/components/ContentSummary"
import { BlockComponent } from "../BlocksRenderer"
import { ImmutableGridSlot } from "./ImmutableGridSlot"
import { MutableGridSlot } from "./MutableGridSlot"
import { imageSizes } from "common/lib/imageSizes"
import { usePageProps } from "common/hooks/data/usePageProps"
import { EditPageProps } from "pages/edit/index.page"
import { ContentSummaryFrontendBlock } from "modules/editor/blocks/ContentSummary"
import type { DragItem } from "./types"

type Columns = number

// Currently only columns 4 & 5 are supported for content summaries & custom_urls
export enum SupportedContentSummaryGridColumns {
  FOUR = 4,
  FIVE = 5,
}

const slotSizeToContentSummaryPropsMap: Record<
  Columns,
  Record<GridSlotSize, Partial<ContentSummaryFrontendBlock>>
> = {
  [SupportedContentSummaryGridColumns.FOUR]: {
    [GridSlotSize.Base]: {
      imageSizes: imageSizes({ ratio: 0.25, mobileRatio: 0.25 }),
      variant: ContentSummaryVariant.Base,
    },
    [GridSlotSize.BaseWide]: {
      imageSizes: imageSizes({ ratio: 0.125, mobileRatio: 0.25 }),
      variant: ContentSummaryVariant.BaseWide,
    },
    [GridSlotSize.Lg]: {
      imageSizes: imageSizes({ ratio: 0.5, mobileRatio: 1 }),
      imageAspectRatio: ImageAspectRatio.Square,
      variant: ContentSummaryVariant.Hero,
    },
    [GridSlotSize.Xl]: {
      imageSizes: imageSizes({ ratio: 0.5, mobileRatio: 1 }),
      variant: ContentSummaryVariant.Hero,
    },
    [GridSlotSize.Xxl]: {
      imageSizes: imageSizes({ ratio: 1, mobileRatio: 1 }),
      variant: ContentSummaryVariant.Hero,
    },
  },
  [SupportedContentSummaryGridColumns.FIVE]: {
    [GridSlotSize.Base]: {
      imageSizes: imageSizes({ ratio: 0.2, mobileRatio: 0.25 }),
      variant: ContentSummaryVariant.Base,
      narrow: true,
    },
    [GridSlotSize.BaseWide]: {
      imageSizes: imageSizes({ ratio: 0.1, mobileRatio: 0.25 }),
      variant: ContentSummaryVariant.BaseWide,
      narrow: true,
    },
    [GridSlotSize.Lg]: {
      imageSizes: imageSizes({ ratio: 0.4, mobileRatio: 1 }),
      variant: ContentSummaryVariant.Lg,
      narrow: true,
    },
    [GridSlotSize.Xl]: {
      imageSizes: imageSizes({ ratio: 0.4, mobileRatio: 1 }),
      imageAspectRatio: ImageAspectRatio.Square,
      variant: ContentSummaryVariant.Hero,
      narrow: true,
    },
    [GridSlotSize.Xxl]: {
      imageSizes: imageSizes({ ratio: 1, mobileRatio: 1 }),
      variant: ContentSummaryVariant.Hero,
      narrow: true,
    },
  },
}

/**
 * Configuration for blocks based on the slot size and columns.
 *
 * This function adds additional props to the blocks based on the slot size the slot size they are positioned.
 * These additional props can define what variant, image aspect ratio and image sizes the block should use.
 *
 * The extra props will only be passed to the block if the block is a ContentSummary or CustomURL block, otherwise
 * the block will be returned as is.
 */
const getAdjustedBlocksConfig = (
  blocks: FrontendBlock[],
  slotSizes: GridSlotSize[],
  columns: Columns,
): FrontendBlock[] =>
  slotSizes.map((slotSize, index: number) => {
    const block = blocks[index] || null
    if (block?.type === "CONTENT_SUMMARY" || block?.type === "CUSTOM_URL") {
      return {
        ...block,
        ...slotSizeToContentSummaryPropsMap[columns][slotSize],
      }
    }

    return block
  })

export interface GridProps extends Omit<GridBlock, "blocks"> {
  blocks: FrontendBlock[]
  columns: Columns
  slotSizes: GridSlotSize[]
  nodeKey?: NodeKey
  onGridSlotClear?: (index: number) => void
  onGridSlotDrop?: (index: number, item: DragItem) => void
  onGridSlotSwap?: (a: number, b: number, item: DragItem) => void
  onGridSlotEdit?: (index: number) => void
  onGridSlotAdd?: (index: number) => void
}

export const Grid = ({
  blocks = [],
  slotSizes = [],
  columns = 4,
  nodeKey,
  onGridSlotClear,
  onGridSlotDrop,
  onGridSlotSwap,
  onGridSlotEdit,
  onGridSlotAdd,
}: GridProps) => {
  const { editing } = usePageProps<EditPageProps>()

  return (
    <section
      className={clsx("grid grid-cols-2 gap-4 sm:gap-6", {
        "sm:grid-cols-4": columns === 4,
        "sm:grid-flow-col sm:grid-cols-5": columns === 5,
        "mb-4": editing || blocks?.length > 0,
      })}
      data-testid="grid"
    >
      {getAdjustedBlocksConfig(blocks, slotSizes, columns).map((block, index) => {
        if (editing) {
          return block === null ? (
            <MutableGridSlot
              key={index}
              gridNodeKey={nodeKey}
              index={index}
              size={slotSizes[index]}
              onDrop={onGridSlotDrop}
              onSwap={onGridSlotSwap}
              onAdd={onGridSlotAdd}
              asPlaceholder
            />
          ) : (
            <MutableGridSlot
              key={index}
              gridNodeKey={nodeKey}
              index={index}
              size={slotSizes[index]}
              onClear={onGridSlotClear}
              onDrop={onGridSlotDrop}
              onSwap={onGridSlotSwap}
              onEdit={onGridSlotEdit}
            >
              <BlockComponent block={block} />
            </MutableGridSlot>
          )
        }
        return block === null ? null : (
          <ImmutableGridSlot key={index} size={slotSizes[index]}>
            <BlockComponent block={block} />
          </ImmutableGridSlot>
        )
      })}
    </section>
  )
}
